import React, { useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';

/* ── SVG Icons ── */
const SemesterIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
  </svg>
);

const StudyToolsIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 2l1 2.5L13 5.5l-2.5 1L9.5 9l-1-2.5L6 5.5l2.5-1z" />
    <path d="M19 8l.75 1.5L21.5 10.5l-1.75.75L19 13l-.75-1.75-1.75-.75 1.75-.75z" />
    <line x1="4" y1="22" x2="15" y2="11" />
    <path d="M14.5 6.5l3 3" />
    <path d="M13 8L16 5a1.41 1.41 0 0 1 2 0l1 1a1.41 1.41 0 0 1 0 2l-3 3" />
  </svg>
);

const OrgAdminIcon = () => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const INTENT_BUILD = 'build_semester';
const INTENT_TOOLS = 'use_tools';
const INTENT_ORG = 'setup_organization';

/* ── Floating particles (stable positions, generated once) ── */
const PARTICLE_COUNT = 12;
function generateParticles() {
  const particles = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      id: i,
      left: `${8 + Math.random() * 84}%`,
      top: `${5 + Math.random() * 90}%`,
      size: 2 + Math.random() * 3,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 3,
      drift: 6 + Math.random() * 10,
      opacity: 0.12 + Math.random() * 0.18,
    });
  }
  return particles;
}

/**
 * Step 1 — Welcome (Premium v4)
 * Tiles are the primary action — click = immediate navigation.
 */
export default function WelcomeStep({ data, goNext, onSkipToTools, onSkipToOrg, user, isOrgAdmin }) {
  const firstName = user?.fullName?.split(' ')[0] || user?.firstName || '';
  const particles = useMemo(generateParticles, []);

  const handleTileClick = useCallback((intent) => {
    UsageEventsTracker.recordUserAction('ONBOARDING_INTERACTION', {
      action: 'welcome_intent_selected',
      category: 'onboarding',
      label: intent,
    });

    if (intent === INTENT_TOOLS) {
      if (onSkipToTools) onSkipToTools();
    } else if (intent === INTENT_ORG) {
      if (onSkipToOrg) onSkipToOrg();
    } else {
      goNext();
    }
  }, [goNext, onSkipToTools, onSkipToOrg]);

  return (
    <motion.div
      className="welcome-immersive"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ── Background layers ── */}
      <div className="welcome-bg-glow" aria-hidden="true" />
      <div className="welcome-bg-spotlight" aria-hidden="true" />
      <div className="welcome-bg-vignette" aria-hidden="true" />
      <div className="welcome-bg-diagonal" aria-hidden="true" />

      {/* ── Floating particles ── */}
      <div className="welcome-particles" aria-hidden="true">
        {particles.map((p) => (
          <motion.span
            key={p.id}
            className="welcome-particle"
            style={{
              left: p.left,
              top: p.top,
              width: p.size,
              height: p.size,
              opacity: 0,
            }}
            animate={{
              y: [-p.drift / 2, p.drift / 2, -p.drift / 2],
              opacity: [0, p.opacity, 0],
            }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* ── Cards glow backdrop ── */}
      <div className="welcome-cards-glow" aria-hidden="true" />

      {/* ── Scout hero with idle float ── */}
      <motion.div
        className="welcome-scout-wrapper"
        initial={{ opacity: 0, scale: 0.75, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="welcome-scout-halo" aria-hidden="true" />
        <motion.img
          src="/scout_images/scout_wave_new.png"
          alt="Scout mascot wearing a graduation cap"
          className="welcome-scout-img"
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* ── Headline ── */}
      <motion.div
        className="welcome-headline"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15, ease: 'easeOut' }}
      >
        <h2>
          {firstName
            ? `Hey ${firstName}, welcome to DocuHounds!`
            : 'Welcome to DocuHounds!'}
        </h2>
        <p className="welcome-subtitle-primary">How do you want to get started?</p>
        <p className="welcome-subtitle-secondary">Build your full system or jump straight into study tools.</p>
      </motion.div>

      {/* ── Accent divider ── */}
      <motion.div
        className="welcome-divider"
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
        aria-hidden="true"
      />

      {/* ── Microcopy ── */}
      <motion.p
        className="welcome-microcopy"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.28, ease: 'easeOut' }}
      >
        Select how you want to start
      </motion.p>

      {/* ── Intent tiles (click = immediate navigation) ── */}
      <div className="welcome-intent-cards" role="group" aria-label="Choose how to get started">
        <motion.button
          type="button"
          className="welcome-intent-tile"
          onClick={() => handleTileClick(INTENT_BUILD)}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          whileTap={{ scale: 0.97 }}
        >
          <div className="welcome-tile-inner-glow" aria-hidden="true" />
          <div className="welcome-tile-icon">
            <SemesterIcon />
          </div>
          <h4>Build your semester</h4>
          <p>Upload your syllabus and we'll organize your entire schedule, assignments, and deadlines.</p>
          <span className="welcome-tile-badge">Best for staying on top of everything</span>
        </motion.button>

        <motion.button
          type="button"
          className="welcome-intent-tile"
          onClick={() => handleTileClick(INTENT_TOOLS)}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          whileTap={{ scale: 0.97 }}
        >
          <div className="welcome-tile-inner-glow" aria-hidden="true" />
          <div className="welcome-tile-icon">
            <StudyToolsIcon />
          </div>
          <h4>Use study tools instantly</h4>
          <p>Generate flashcards, quizzes, and more right away. No setup required.</p>
          <span className="welcome-tile-badge">Best for quick help right now</span>
        </motion.button>

        {isOrgAdmin && (
          <motion.button
            type="button"
            className="welcome-intent-tile welcome-intent-tile--org"
            onClick={() => handleTileClick(INTENT_ORG)}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            whileTap={{ scale: 0.97 }}
          >
            <div className="welcome-tile-inner-glow" aria-hidden="true" />
            <div className="welcome-tile-icon">
              <OrgAdminIcon />
            </div>
            <h4>Set up my organization</h4>
            <p>Create courses for your students, manage members, and configure your organization.</p>
            <span className="welcome-tile-badge">Best for instructors &amp; admins</span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}
